import React from "react";
import { observer, inject } from "mobx-react";
import qs from "qs";
import Pagination from "components/Pagination";
import Footer from "components/Footer";
import { stripHtml } from "helpers/strings";
import { currencyFormattedAmount } from "helpers/currency";
import * as DataList from "components/DataList";
import { shortDate, clockTime } from "components/Tables/ScheduleList";
import { COLUMNS as MAIN_COLUMNS, CARDS as MAIN_CARDS } from "components/Tables/WorkReports";
let location_id = "";

// A client's work orders (client page, Work orders tab), laid out like the
// Work orders list (components/Tables/WorkReports). Every row belongs to this
// client, so the customer column becomes the work order's location.
const LOCATION_COLUMN = { area: "where", label: "Location", width: "minmax(170px, 1.3fr)", icon: "map-marker" };

const COLUMNS = MAIN_COLUMNS.map((column) => (column.area === "client" ? LOCATION_COLUMN : column));

const CARDS = {
  ...MAIN_CARDS,
  areas: MAIN_CARDS.areas.map((row) => row.replace(/\bclient\b/g, "where")),
};

const MIN_TABLE_WIDTH = 1330;

const STATUS_TONES = {
  draft: "grey",
  submitted: "blue",
  reviewed: "purple",
  invoiced: "green",
};

const capitalise = (text) => (text ? text.charAt(0).toUpperCase() + text.slice(1) : "");

@inject("viewStore", "workReportStore")
@observer
class WorkOrdersTable extends React.Component {
  // The store starts out not loading; until this tab has asked for its work
  // orders, show the skeleton rather than a flash of the empty state.
  requested = false;

  componentDidMount() {
    this.requested = true;
    this.props.viewStore.clientWorkReportSearch.where.client_id =
      this.props.client.attributes.id;
    var params = new URLSearchParams(window.location.search);
    location_id = params.get("location_id");
    if (location_id) {
      this.props.viewStore.clientWorkReportWithLocationSearch.where.location_id =
        location_id;
      this.props.viewStore.clientWorkReportWithLocationSearch.where.client_id =
        this.props.client.attributes.id;
      this.props.workReportStore.loadClientWorkReportsWithLocation();
    } else {
      this.props.viewStore.clientWorkReportSearch.where.client_id =
        this.props.client.attributes.id;
      this.props.workReportStore.loadClientWorkReports();
    }
  }

  onPaginationChange(newPage) {
    var params = new URLSearchParams(window.location.search);
    location_id = params.get("location_id");
    let {
      viewStore: { clientWorkReportSearch, clientWorkReportWithLocationSearch },
    } = this.props;
    if (location_id) {
      this.props.viewStore.clientWorkReportWithLocationSearch = {
        ...clientWorkReportWithLocationSearch,
        page: newPage,
      };
      this.props.workReportStore.loadClientWorkReportsWithLocation();
    } else {
      this.props.viewStore.clientWorkReportSearch = {
        ...clientWorkReportSearch,
        page: newPage,
      };
      this.props.workReportStore.loadClientWorkReports();
    }
  }

  render() {
    const { objects, isLoading, isError, meta } =
      this.props.workReportStore.clientWorkReports;
    const {
      client,
      viewStore,
      viewStore: {
        jobTab: { section },
      },
    } = this.props;
    const byLocation = Boolean(new URLSearchParams(window.location.search).get("location_id"));
    const search = byLocation
      ? viewStore.clientWorkReportWithLocationSearch
      : viewStore.clientWorkReportSearch;

    // The store's client work orders are shared between client pages; rows
    // left over from another client aren't shown while this client's load.
    const first = objects[0];
    const stale = Boolean(first && first.attributes.client && first.attributes.client.id
      && first.attributes.client.id !== client.attributes.id);

    if ((isLoading || !this.requested) && (objects.length === 0 || stale)) {
      return (
        <DataList.Loading columns={COLUMNS} cards={CARDS} minTableWidth={MIN_TABLE_WIDTH} noun="work orders" rows={5} />
      );
    }
    if (isError) {
      return (
        <DataList.LoadError
          noun="work orders"
          onRetry={() => this.onPaginationChange((meta && meta.page) || 1)}
        />
      );
    }
    if (objects.length === 0) {
      return (
        <DataList.Empty
          noun="work orders"
          message={byLocation
            ? "Work orders created for this location will show up here."
            : "Work orders created for this client will show up here."}
        />
      );
    }

    const pagination = meta && meta.total_pages > 1 && (
      <Footer>
        <div className="bp3-navbar-group bp3-align-left">
          <Pagination
            value={meta.page}
            total={meta.total_pages}
            onChange={this.onPaginationChange.bind(this)}
          />
        </div>
      </Footer>
    );

    return (
      <React.Fragment>
        <DataList.List
          preferTable
          columns={COLUMNS}
          cards={CARDS}
          minTableWidth={MIN_TABLE_WIDTH}
          total={(meta && meta.total_records) || objects.length}
          noun="work orders"
          note={DataList.sortNote(search.order)}
        >
          {objects.map((workReport) => {
            const attributes = workReport.attributes;
            const job = attributes.job || {};
            const createdBy = attributes.created_by || {};
            const deleted = Boolean(attributes.deleted_at);
            const description = attributes.description ? stripHtml(attributes.description).trim() : "";
            const address = attributes.location && attributes.location.full_address;
            const showLink = `/dashboard/work-reports/${workReport.id}/show`;
            const jobLink = job.id ? `/dashboard/jobs/${job.id}/${section}` : null;
            return (
              <DataList.Row key={workReport.id}>
                <DataList.Cell area="number">
                  <div className="dl-lines">
                    <DataList.RowLink href={showLink} label={`Work order ${attributes.number}`}>
                      #{attributes.number}
                    </DataList.RowLink>
                    {attributes.invoice_number && (
                      <div className="dl-muted dl-truncate">Invoice {attributes.invoice_number}</div>
                    )}
                  </div>
                </DataList.Cell>
                <DataList.Cell area="job">
                  {attributes.event_number ? (
                    <DataList.Lines
                      primary={`Job #${attributes.event_number}`}
                      href={jobLink || undefined}
                      secondary={job.service_type_name}
                    />
                  ) : (
                    <DataList.Muted>No job</DataList.Muted>
                  )}
                </DataList.Cell>
                <DataList.Cell area="date">
                  <DataList.Lines
                    primary={<React.Fragment><DataList.CardOnly>Created </DataList.CardOnly>{shortDate(attributes.created_at)}</React.Fragment>}
                    secondary={clockTime(attributes.created_at)}
                    strong={false}
                    title={shortDate(attributes.created_at)}
                  />
                </DataList.Cell>
                <DataList.Cell area="where">
                  {address ? (
                    <DataList.Lines primary={address} strong={false} />
                  ) : (
                    <DataList.Muted>No location</DataList.Muted>
                  )}
                </DataList.Cell>
                <DataList.Cell area="author">
                  {createdBy.full_name ? (
                    <DataList.Lines
                      primary={createdBy.full_name}
                      href={createdBy.id
                        ? `/dashboard/users/${createdBy.id}/show?${qs.stringify(viewStore.userShowSearch, { encode: false })}`
                        : undefined}
                      secondary={createdBy.email}
                    />
                  ) : (
                    <DataList.Muted>Unknown</DataList.Muted>
                  )}
                </DataList.Cell>
                <DataList.Cell area="details">
                  <DataList.Lines
                    primary={description || <DataList.Muted>No description</DataList.Muted>}
                    title={description}
                    strong={false}
                    secondary={attributes.po_number ? `PO ${attributes.po_number}` : <DataList.Muted>No PO number</DataList.Muted>}
                  />
                </DataList.Cell>
                <DataList.Cell area="total">
                  <div className="dl-lines__primary">
                    <DataList.CardOnly>Total </DataList.CardOnly>
                    {currencyFormattedAmount(attributes.grand_total)}
                  </div>
                </DataList.Cell>
                <DataList.Cell area="status">
                  {deleted ? (
                    <DataList.Pill tone="red">Deleted</DataList.Pill>
                  ) : (
                    <DataList.Pill tone={STATUS_TONES[attributes.status] || "grey"}>
                      {capitalise(attributes.status)}
                    </DataList.Pill>
                  )}
                </DataList.Cell>
                <DataList.Cell area="actions">
                  <DataList.Actions
                    items={[
                      jobLink && { icon: "eye-open", label: "View job", href: jobLink },
                      { icon: "edit", label: "View or edit work order", href: showLink },
                    ]}
                  />
                </DataList.Cell>
              </DataList.Row>
            );
          })}
        </DataList.List>
        {pagination}
      </React.Fragment>
    );
  }
}

export default WorkOrdersTable;
